import { Package, ReceiptEuro, TriangleAlert, WeightTilde } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  calculateDeliveryContentMinimumWeightKg,
  calculateExpeditionPackagingTotalWeightKg,
} from "@/lib/fedex-weight.util";
import { roundNumberToFixed } from "@/lib/money.util";
import { cn } from "@/lib/utils";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";

export const FedexApiPackageTotal = () => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<QuotationUpdateBoFormData>();

  const watch = useWatch({
    control: methods.control,
    name: "expeditionFedexApi.expeditionPackaging",
  });

  const totalPackages = roundNumberToFixed(
    watch?.reduce((acc: number, item) => acc + Number(item.count || 0), 0),
    2,
  );

  const totalWeightValue = calculateExpeditionPackagingTotalWeightKg(watch ?? []);

  const totalWeight = roundNumberToFixed(totalWeightValue, 2) + " kg";

  const totalInsuranceValue = roundNumberToFixed(
    watch?.reduce(
      (acc: number, item) => acc + Number(item.value || 0) * Number(item.count || 0),
      0,
    ),
    2,
  );

  const totalInsurance = totalInsuranceValue + " €";

  const deliveryContent = useWatch({
    control: methods.control,
    name: "deliveryContent",
  });

  const totalPriceValue = roundNumberToFixed(
    deliveryContent?.reduce(
      (acc: number, item) => acc + Number(item.unitPrice || 0) * Number(item.numberOfBottles || 0),
      0,
    ),
    2,
  );

  const totalPrice = totalPriceValue + " €";

  // Mirrors the packaging weight rule enforced by the form schema.
  const minimumWeightValue = roundNumberToFixed(
    calculateDeliveryContentMinimumWeightKg(deliveryContent ?? []),
    2,
  );

  const isWeightUnderContent = totalWeightValue < minimumWeightValue;

  const insuranceWarning =
    totalInsuranceValue > totalPriceValue
      ? {
          className: "text-red-600",
          message: t("fedexPackageTotal.insuranceAboveGoodsPrice"),
        }
      : totalInsuranceValue < totalPriceValue
        ? {
            className: "text-amber-600",
            message: t("fedexPackageTotal.insuranceBelowGoodsPrice"),
          }
        : null;

  return (
    <div className="grid grid-cols-2 gap-2 @2xl:grid-cols-[repeat(4,auto)]">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-black">{t("fedexPackageTotal.packageCount")}</p>
        <div className="flex flex-row items-center gap-2">
          <Package className="h-6 w-6 shrink-0 text-black" />
          <p className="text-md text-gray-900">{totalPackages}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-l-2 border-gray-200 pl-2">
        <p className="text-sm text-black">{t("fedexPackageTotal.totalWeight")}</p>
        <div className="flex flex-row items-center gap-2">
          <WeightTilde
            className={cn("h-6 w-6 shrink-0 text-black", isWeightUnderContent && "text-red-600")}
          />
          <p className={cn("text-md text-gray-900", isWeightUnderContent && "text-red-600")}>
            {totalWeight}
          </p>
          {isWeightUnderContent && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TriangleAlert className="size-4 shrink-0 cursor-help text-red-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-64 text-sm">
                    {t("fedexPackageTotal.weightUnderContent", {
                      minimumWeight: minimumWeightValue,
                    })}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 border-gray-200 @2xl:border-l-2 @2xl:pl-2">
        <p className="text-sm text-black">{t("fedexPackageTotal.totalInsurance")}</p>
        <div className="flex flex-row items-center gap-2">
          <ReceiptEuro className={cn("h-6 w-6 shrink-0 text-black", insuranceWarning?.className)} />
          <p className={cn("text-md text-gray-900", insuranceWarning?.className)}>
            {totalInsurance}
          </p>
          {insuranceWarning && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TriangleAlert
                    className={cn("size-4 shrink-0 cursor-help", insuranceWarning.className)}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-64 text-sm">{insuranceWarning.message}</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 border-l-2 border-gray-200 pl-2">
        <p className="text-sm text-black">{t("fedexPackageTotal.totalPrice")}</p>
        <div className="flex flex-row items-center gap-2">
          <ReceiptEuro className="h-6 w-6 shrink-0 text-black" />
          <p className="text-md text-gray-900">{totalPrice}</p>
        </div>
      </div>
    </div>
  );
};
